import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ConfigProvider, Empty, Form, Input, Modal, Popconfirm, Switch } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import CapMark from 'components/Global/ChatBubble/CapMark';
import { deleteApi, getApi, patchApi, postApi, putApi } from 'redux/apis';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';

export interface Skill {
	id: string;
	name: string;
	description: string;
	instructions: string;
	enabled: boolean;
	sortId: number;
}

interface SkillsManagerProps {
	open: boolean;
	onClose: () => void;
	companyId: string | null;
}

interface SkillFormValues {
	name: string;
	description: string;
	instructions: string;
}

// CAP brand palette (from the chat panel / sidebar): dark green on warm cream.
const GREEN = '#2e4536';
const GREEN_DEEP = '#26301f';

// Manages the CAP Assistant's per-company skills — the playbooks it follows when
// a user's question matches one. Opened from the chat sidebar next to "New
// chat". Styled to match the chat panel (cream + dark green, Poppins): a list
// you can add to, edit, enable/disable, and delete; the assistant picks up the
// changes on its next reply (no restart).
const SkillsManager = ({ open, onClose, companyId }: SkillsManagerProps) => {
	const [skills, setSkills] = useState<Skill[]>([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	// null = list view; an object (or 'new') = the add/edit form is open.
	const [editing, setEditing] = useState<Skill | 'new' | null>(null);
	const [togglingId, setTogglingId] = useState<string | null>(null);
	const [form] = Form.useForm<SkillFormValues>();
	// The non-scrolling modal content box. Delete-confirm popovers render into it
	// so they layer above the modal card and are never clipped by the scrolling
	// skill list or lost behind the modal mask.
	const contentRef = useRef<HTMLDivElement>(null);

	const loadSkills = useCallback(() => {
		if (!companyId) return;
		setLoading(true);
		getApi('/ai/skills', { companyId })
			.then((res) => {
				const list = res?.data?.data?.skills;
				if (Array.isArray(list)) setSkills(list);
			})
			.catch(() => toastText('Could not load skills.', 'error'))
			.finally(() => setLoading(false));
	}, [companyId]);

	useEffect(() => {
		if (open) {
			setEditing(null);
			loadSkills();
		}
	}, [open, loadSkills]);

	const openForm = (skill: Skill | 'new') => {
		setEditing(skill);
		if (skill === 'new') {
			form.resetFields();
		} else {
			form.setFieldsValue({
				name: skill.name,
				description: skill.description,
				instructions: skill.instructions,
			});
		}
	};

	const handleSave = async (values: SkillFormValues) => {
		if (!companyId) return;
		setSaving(true);
		try {
			if (editing === 'new') {
				await postApi('/ai/skills', { ...values, companyId });
				toastText('Skill created.', 'success');
			} else if (editing) {
				await putApi(`/ai/skills/${editing.id}`, { ...values, companyId });
				toastText('Skill updated.', 'success');
			}
			setEditing(null);
			loadSkills();
		} catch (err: any) {
			toastText(err?.response?.data?.message || 'Could not save the skill.', 'error');
		} finally {
			setSaving(false);
		}
	};

	const handleToggle = async (skill: Skill, enabled: boolean) => {
		if (!companyId) return;
		setTogglingId(skill.id);
		// Optimistic flip, reverted by a refetch on failure.
		setSkills((prev) => prev.map((s) => (s.id === skill.id ? { ...s, enabled } : s)));
		try {
			await patchApi(`/ai/skills/${skill.id}/enabled`, { companyId, enabled });
		} catch {
			toastText('Could not update the skill.', 'error');
			loadSkills();
		} finally {
			setTogglingId(null);
		}
	};

	const handleDelete = async (skill: Skill) => {
		if (!companyId) return;
		setSkills((prev) => prev.filter((s) => s.id !== skill.id));
		try {
			await deleteApi(`/ai/skills/${skill.id}?companyId=${companyId}`);
			toastText('Skill deleted.', 'success');
		} catch {
			toastText('Could not delete the skill.', 'error');
			loadSkills();
		}
	};

	const inForm = editing !== null;
	const enabledCount = skills.filter((s) => s.enabled).length;

	return (
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: GREEN,
					colorPrimaryHover: GREEN_DEEP,
					borderRadius: 12,
					fontFamily: "'Poppins', sans-serif",
				},
			}}
		>
			<Modal
				open={open}
				onCancel={onClose}
				footer={null}
				width={660}
				centered
				closable={false}
				className={styles['skills__modal']}
				styles={{
					content: { padding: 0, borderRadius: 18, overflow: 'hidden' },
					body: { padding: 0 },
				}}
				destroyOnClose
			>
				<div className={styles['skills']}>
					<header className={styles['skills__header']}>
						<div className={styles['skills__heading']}>
							<span className={styles['skills__mark']}>
								<CapMark size={18} tone="light" />
							</span>
							<div>
								<h2 className={styles['skills__title']}>
									{inForm ? (editing === 'new' ? 'Add skill' : 'Edit skill') : 'Assistant skills'}
								</h2>
								<p className={styles['skills__subtitle']}>
									{inForm
										? 'The assistant follows this whenever a matching question comes up.'
										: `How the CAP Assistant answers for this company · ${enabledCount} active`}
								</p>
							</div>
						</div>
						<div className={styles['skills__header-actions']}>
							{!inForm && (
								<Button type="primary" icon={<PlusOutlined />} onClick={() => openForm('new')} className={styles['skills__add']}>
									Add skill
								</Button>
							)}
							<button type="button" className={styles['skills__close']} onClick={onClose} aria-label="Close">
								×
							</button>
						</div>
					</header>

					<div className={styles['skills__content']} ref={contentRef}>
						{inForm ? (
							<Form form={form} layout="vertical" onFinish={handleSave} className={styles['skills__form']} requiredMark={false}>
								<Form.Item
									label="Name"
									name="name"
									rules={[{ required: true, message: 'Give the skill a short name.' }]}
									extra="Short and unique. Spaces become hyphens, e.g. “allocate pay period” → allocate-pay-period."
								>
									<Input placeholder="allocate-pay-period" maxLength={80} size="large" />
								</Form.Item>
								<Form.Item
									label="When to use it"
									name="description"
									rules={[{ required: true, message: 'Describe when this skill applies.' }]}
									extra="One line — this is what the assistant reads to decide whether to use the skill."
								>
									<Input placeholder="The user wants to allocate or post a pay period." maxLength={300} size="large" />
								</Form.Item>
								<Form.Item
									label="Instructions"
									name="instructions"
									rules={[{ required: true, message: 'Add the instructions the assistant should follow.' }]}
									extra="The full guidance it follows. End by suggesting the next step and linking the relevant page."
								>
									<Input.TextArea rows={8} maxLength={8000} placeholder="Walk the user through…" />
								</Form.Item>
								<div className={styles['skills__form-actions']}>
									<Button onClick={() => setEditing(null)} disabled={saving} size="large">
										Cancel
									</Button>
									<Button type="primary" htmlType="submit" loading={saving} size="large">
										{editing === 'new' ? 'Create skill' : 'Save changes'}
									</Button>
								</div>
							</Form>
						) : loading ? (
							<div className={styles['skills__list']}>
								{Array.from({ length: 4 }).map((_, i) => (
									<div key={i} className={styles['skills__skeleton']} aria-hidden="true" />
								))}
							</div>
						) : skills.length === 0 ? (
							<div className={styles['skills__empty']}>
								<Empty description="No skills yet — add one to shape how the assistant answers." />
							</div>
						) : (
							<div className={styles['skills__list']}>
								{skills.map((skill) => (
									<div
										key={skill.id}
										className={`${styles['skills__row']} ${!skill.enabled ? styles['skills__row--off'] : ''}`}
									>
										<div className={styles['skills__row-main']}>
											<div className={styles['skills__row-name']}>{skill.name}</div>
											<div className={styles['skills__row-desc']}>{skill.description}</div>
										</div>
										<div className={styles['skills__row-actions']}>
											<Switch
												checked={skill.enabled}
												loading={togglingId === skill.id}
												onChange={(checked) => handleToggle(skill, checked)}
											/>
											<button type="button" className={styles['skills__icon-btn']} title="Edit" onClick={() => openForm(skill)}>
												<EditOutlined />
											</button>
											<Popconfirm
												title="Delete this skill?"
												description="The assistant will stop using it."
												okText="Delete"
												cancelText="Cancel"
												okButtonProps={{ danger: true }}
												placement="topRight"
												// Render into the modal's own content box (not a body-level
												// portal): keeps the confirm above the modal card and never
												// behind the mask, while staying within the card so it isn't
												// clipped by the scrolling list.
												getPopupContainer={() => contentRef.current || document.body}
												onConfirm={() => handleDelete(skill)}
											>
												<button type="button" className={`${styles['skills__icon-btn']} ${styles['skills__icon-btn--danger']}`} title="Delete">
													<DeleteOutlined />
												</button>
											</Popconfirm>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</Modal>
		</ConfigProvider>
	);
};

export default SkillsManager;
